import { Box, Button, Divider, Stack, TextField, Typography } from '@mui/material';
import type { InviteUserPayload, InviteUserResponse } from '@weft/api-definition';
import { InviteUserResponseSchema } from '@weft/api-definition';
import { VyIcon } from '@weft/icons';
import { validateNotNullOrUndefined } from '@weft/utils';
import React, { useState } from 'react';
import type { UseMutationOptions } from 'react-query';
import { useMutation } from 'react-query';
import { z } from 'zod';
import { VyTable } from '../../../../components/new-design/table/vy-table/VyTable';
import { useUsers } from '../../../../hooks/requests/use-users';
import { getPrivateApi } from '../../../../services/api';
import { formatDate } from '../../../../utils/date';
import { runMutation } from '../../../../utils/vy-mutation';

function useInviteUser(options?: UseMutationOptions<InviteUserResponse, unknown, InviteUserPayload>) {
  return useMutation({
    mutationFn: async ({ username }) => {
      const api = getPrivateApi();

      const inviteUserResponse = await api.inviteUser({
        inviteUserRequest: {
          username,
        },
      });

      return InviteUserResponseSchema.parse(inviteUserResponse satisfies InviteUserResponse);
    },
    ...options,
  });
}

export const MembersTabContent: React.FC = () => {
  const inviteUserMutation = useInviteUser();

  const { data } = useUsers({ suspense: true });
  validateNotNullOrUndefined(data);

  const { users } = data;

  const [newMemberEmail, setNewMemberEmail] = useState('');

  const handleNewMemberEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewMemberEmail(event.target.value);
  };

  const handleInviteMember = async () => {
    await runMutation(
      inviteUserMutation,
      { username: newMemberEmail },
      {
        pending: 'Inviting member...',
        success: 'Member invited successfully',
        error: 'Failed to invite member',
      },
    );
  };

  return (
    <Stack
      direction="column"
      paddingY="20px"
      divider={<Divider sx={{ marginY: '20px' }}/>}
    >
      <Stack
        direction="column"
        display="flex"
        paddingX="20px"
      >
        <Typography variant="h6">Members Management</Typography>
        <Typography
          variant="subtitle1"
          color="text.secondary"
        >
          Manage your team members here
        </Typography>
      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        maxWidth="lg"
        display="flex"
        paddingX="20px"
      >
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 7,
        }}
        >
          <Typography >
            Invite a new member
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
          >
            Invite new members by email address
          </Typography>
        </Box>

        <Box sx={{
          display: 'flex',
          flex: 8,
          columnGap: '16px',
        }}
        >
          <TextField
            size="small"
            placeholder="example@mail.com"
            fullWidth
            onChange={handleNewMemberEmailChange}
          />

          <Button
            variant="contained"
            size="small"
            sx={{
              minWidth: 'max-content',
              marginTop: '4px',
              columnGap: '8px',
            }}
            disabled={!z.string().email().safeParse(newMemberEmail).success}
            onClick={handleInviteMember}
          >
            <VyIcon icon="AddUser"/>
            Invite member
          </Button>
        </Box>
      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        maxWidth="lg"
        display="flex"
        paddingX="20px"
      >
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 7,
        }}
        >
          <Typography >
            Team members
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
          >
            The members in your organization
          </Typography>
        </Box>

        <Box sx={{
          display: 'flex',
          flex: 8,
          overflow: 'hidden',
          minHeight: '300px',
        }}
        >
          <VyTable
            itemsCount={users.length}
            items={users}
            columns={[
              {
                field: 'email',
                headerName: 'Status',
                flex: 1,
                headerClassName: 'pl-6',
                cellClassName: 'pl-6',
              },
              {
                field: 'createdAt',
                headerName: 'Date Joined',
                flex: 1,
                headerClassName: 'pl-6',
                cellClassName: 'pl-6',
                valueGetter: ({ row }) => formatDate(row.createdAt),
              },
            ]}
            showPagination={false}
          />
        </Box>
      </Stack>
    </Stack>
  );
};